import { useState, useEffect, useMemo } from 'react';
import styles from '../Universidades/Formulario.module.css';
import Swal from 'sweetalert2';
import {useAuth} from '../../Auth/AuthContext'; 

const AniadirBeca = ({ abrirModal, cerrarModal, becaAEditar = null, onBecaGuardada }) => {
    const {getToken} = useAuth(); 
    const token = getToken(); 
    const [formData, setFormData] = useState({
        nombre: '',
        tipo: 1,
        info: '',
        link: '',
        fecha_inicio: '',
        fecha_final: '',
        universidad: '' 
    });

    const [errores, setErrores] = useState({});
    const [isClosing, setIsClosing] = useState(false);
    const [universidades, setUniversidades] = useState([]); 

    // Tipos de becas
    const tiposBecas = [
        { value: 1, label: 'Estatal' },
        { value: 2, label: 'Federal' },
        { value: 3, label: 'Municipal' },
        { value: 4, label: 'Escolar' },
        { value: 5, label: 'Otra'}
    ];

    useEffect(() => {
        const cargarUniversidades = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/universidades/obtener-universidades`, {
                });

                if (response.ok) {
                    const data = await response.json();
                    setUniversidades(data);
                }
            } catch (error) {
                console.error('Error al cargar universidades:', error);
            }
        };

        if (abrirModal) {
            cargarUniversidades();
        }
    }, [abrirModal]);

    useEffect(() => {
        if (becaAEditar) {
            setFormData({
                nombre: becaAEditar.nombre || '',
                tipo: becaAEditar.tipo || 1,
                info: becaAEditar.info || '',
                link: becaAEditar.link || '',
                fecha_inicio: becaAEditar.fecha_inicio ? becaAEditar.fecha_inicio.split('T')[0] : '',
                fecha_final: becaAEditar.fecha_final ? becaAEditar.fecha_final.split('T')[0] : '',
                universidad: becaAEditar.universidad || '' 
            });
        } else {
            setFormData({
                nombre: '',
                tipo: 1,
                info: '',
                link: '',
                fecha_inicio: '',
                fecha_final: '',
                universidad: '' 
            });
        }
        setErrores({});
    }, [becaAEditar, abrirModal]);

    // Validaciones
    const validators = useMemo(() => ({
        requerido: (valor, campo) => {
            return !valor?.trim() ? `${campo} es requerido.` : '';
        },
        url: (url) => {
            if (!url?.trim()) return 'El enlace es requerido.';
            try {
                new URL(url);
                return '';
            } catch {
                return 'El enlace no es válido. Debe incluir http:// o https://';
            }
        },
        fecha: (fechaInicio, fechaFinal) => {
            if (!fechaInicio) return 'La fecha de inicio es requerida.';
            if (!fechaFinal) return 'La fecha final es requerida.';
            if (new Date(fechaInicio) >= new Date(fechaFinal)) {
                return 'La fecha de inicio debe ser anterior a la fecha final.';
            }
            return '';
        },
        universidad: (tipo, universidad) => {
            if (tipo === 4 && !universidad) {
                return 'La universidad es requerida para becas escolares.';
            }
            return '';
        }
    }), []);

    const handleCloseModal = () => {
        setIsClosing(true);
    };

    useEffect(() => {
        if (isClosing) {
            const timer = setTimeout(() => {
                cerrarModal();
                setIsClosing(false);
                setFormData({
                    nombre: '',
                    tipo: 1,
                    info: '',
                    link: '',
                    fecha_inicio: '',
                    fecha_final: '',
                    universidad: '' 
                });
                setErrores({});
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isClosing, cerrarModal]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'tipo' && parseInt(value) !== 4) {
            setFormData(prev => ({
                ...prev,
                tipo: parseInt(value),
                universidad: ''
            }));
            setErrores(prev => ({
                ...prev,
                universidad: ''
            }));
        }

        const shouldValidate = field === 'tipo' ? 
            value !== '' && value !== null && value !== undefined : 
            value?.trim() !== '';

        if (shouldValidate) {
            const error = validarCampo(field, value);
            setErrores(prev => ({
                ...prev,
                [field]: error
            }));
        } else {
            setErrores(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validarCampo = (campo, valor) => {
        switch (campo) {
            case 'nombre':
                return validators.requerido(valor, 'El nombre');
            case 'info':
                return validators.requerido(valor, 'La información');
            case 'link':
                return validators.url(valor);
            case 'fecha_inicio':
                return validators.fecha(valor, formData.fecha_final);
            case 'fecha_final':
                return validators.fecha(formData.fecha_inicio, valor);
            case 'universidad': 
                return validators.universidad(formData.tipo, valor);
            default:
                return '';
        }
    };

    const validarFormulario = () => {
        const nuevosErrores = {};

        nuevosErrores.nombre = validarCampo('nombre', formData.nombre);
        nuevosErrores.info = validarCampo('info', formData.info);
        nuevosErrores.link = validarCampo('link', formData.link);
        
        const errorFecha = validators.fecha(formData.fecha_inicio, formData.fecha_final);
        if (errorFecha) {
            nuevosErrores.fecha_inicio = errorFecha;
            nuevosErrores.fecha_final = errorFecha;
        }

        nuevosErrores.universidad = validarCampo('universidad', formData.universidad);

        const erroresFiltrados = Object.fromEntries(
            Object.entries(nuevosErrores).filter(([_, value]) => value !== '')
        );

        setErrores(erroresFiltrados);
        return Object.keys(erroresFiltrados).length === 0;
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCloseModal();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        const datosEnvio = {
            nombre: formData.nombre.trim(),
            tipo: formData.tipo,
            info: formData.info.trim(),
            link: formData.link.trim(),
            fecha_inicio: formData.fecha_inicio,
            fecha_final: formData.fecha_final,
            universidad: formData.tipo === 4 ? formData.universidad : null // NUEVO CAMPO
        };

        try {
            const url = becaAEditar 
                ? `${process.env.REACT_APP_API_URL}/admin/editar-beca/${becaAEditar.id}`
                : `${process.env.REACT_APP_API_URL}/admin/agregar-beca`;
            
            const method = becaAEditar ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(datosEnvio),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || `Error al ${becaAEditar ? 'actualizar' : 'crear'} la beca`);
            }

            await Swal.fire({
                title: `Beca ${becaAEditar ? 'actualizada' : 'creada'} correctamente.`,
                icon: 'success',
                confirmButtonText: 'Continuar',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito',
                },
            });

            if (onBecaGuardada) {
                onBecaGuardada();
            }
            
            cerrarModal();
        } catch (error) {
            await Swal.fire({
                title: 'Error',
                text: error.message || `Ha ocurrido un error al ${becaAEditar ? 'actualizar' : 'crear'} la beca.`,
                icon: 'error',
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#242b57',
                customClass: {
                    popup: 'popupbonito',
                },
            });
        }
    };

    if (!abrirModal && !isClosing) return null;

    return (
        <div className={styles.modalAniadir} onClick={handleOverlayClick}>
            <div className={`${styles.modalScrollableAniadir} ${
                abrirModal && !isClosing ? styles.modalAniadirAnim : isClosing ? styles.modalFadeOut : ''
            }`}>
                    <button onClick={handleCloseModal} type="button" className={styles.closeBtnAniadir}>
                        ×
                    </button>
                    <h2 className={styles.tituloAniadir}>
                        {becaAEditar ? 'Editar beca' : 'Añadir beca'}
                    </h2>
                    <form className={styles.formUsuario} onSubmit={handleSubmit}>

                        <label htmlFor="nombre">
                            Nombre de la beca:
                            <input
                                type="text"
                                id="nombre"
                                value={formData.nombre}
                                placeholder='Ej: "Beca académica"'
                                onChange={(e) => handleInputChange('nombre', e.target.value)}
                                required
                            />
                        </label>
                        {errores.nombre && <p className={styles.mensajeErrorAniadir}>{errores.nombre}</p>}

                        <label htmlFor="tipo">
                            Tipo de beca:
                            <select
                                id="tipo"
                                value={formData.tipo}
                                onChange={(e) => handleInputChange('tipo', parseInt(e.target.value))}
                                required
                            >
                                {tiposBecas.map(tipo => (
                                    <option key={tipo.value} value={tipo.value}>
                                        {tipo.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {formData.tipo === 4 && (
                            <>
                                <label htmlFor="universidad">
                                    Universidad:
                                    <select
                                        id="universidad"
                                        value={formData.universidad}
                                        onChange={(e) => handleInputChange('universidad', e.target.value)}
                                        required
                                    >
                                        <option value="">Selecciona una universidad</option>
                                        {universidades.map(uni => (
                                            <option key={uni.id} value={uni.id}>
                                                {uni.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {errores.universidad && <p className={styles.mensajeErrorAniadir}>{errores.universidad}</p>}
                            </>
                        )}

                        <label htmlFor="info">
                            Información de la beca:
                            <input
                                id="info"
                                value={formData.info}
                                placeholder="Ej: El objetivo de la beca es dar un apoyo a los estudiantes..."
                                onChange={(e) => handleInputChange('info', e.target.value)}
                                rows="4"
                                required
                            />
                        </label>
                        {errores.info && <p className={styles.mensajeErrorAniadir}>{errores.info}</p>}

                        <label htmlFor="link">
                            Enlace de la beca:
                            <input
                                type="url"
                                id="link"
                                value={formData.link}
                                placeholder="Ej: https://ejemplo.com/beca"
                                onChange={(e) => handleInputChange('link', e.target.value)}
                                required
                            />
                        </label>
                        {errores.link && <p className={styles.mensajeErrorAniadir}>{errores.link}</p>}

                        <label htmlFor="fecha_inicio">
                            Fecha de inicio:
                            <input
                                type="date"
                                id="fecha_inicio"
                                value={formData.fecha_inicio}
                                onChange={(e) => handleInputChange('fecha_inicio', e.target.value)}
                                required
                            />
                        </label>

                        <label htmlFor="fecha_final">
                            Fecha final:
                            <input
                                type="date"
                                id="fecha_final"
                                value={formData.fecha_final}
                                onChange={(e) => handleInputChange('fecha_final', e.target.value)}
                                required
                            />
                        </label>
                        {errores.fecha_final && <p className={styles.mensajeErrorAniadir}>{errores.fecha_final}</p>}

                        <button type="submit" className={styles.botonGuard}>
                            {becaAEditar ? 'Actualizar beca' : 'Crear beca'}
                        </button>
                    </form>
                </div>
            </div>
    );
};

export default AniadirBeca;